import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { AppConfig } from "../../../AppConfig";
import { PageRoutes } from "../../../constants/routes";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { Button } from "../../atoms/Button";
import { Checkbox } from "../../atoms/Checkbox";
import { Link } from "../../atoms/Link";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  handleSubmit: () => void;
};

export const AppCouponForm: React.FC<Props> = ({ handleSubmit }) => {
  const schema = useMemo(() => {
    return z
      .object({
        hasChecked: z.boolean(),
      })
      .refine((data) => data.hasChecked);
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  const [viewedTermsOfUse, setViewedTermsOfUse] = useState<string | null>(
    LocalStorageManager.Instance.getObject<string>(
      AppConfig.Instance.LocalStorageKey["viewedTermsOfUse"],
    ),
  );

  const setHasChecked = useCallback(
    (checked: boolean) => {
      if (!methods) return;

      methods.setValue("hasChecked", checked, { shouldValidate: true });
      if (checked) {
        LocalStorageManager.Instance.saveObject<string>(
          AppConfig.Instance.LocalStorageKey["viewedTermsOfUse"],
          new Date().toISOString(),
        );
        setViewedTermsOfUse(
          LocalStorageManager.Instance.getObject<string>(
            AppConfig.Instance.LocalStorageKey["viewedTermsOfUse"],
          ),
        );
      }
    },
    [methods],
  );

  // Reactively update when returning from terms page or in another tab
  useEffect(() => {
    const handleFocus = () => {
      setViewedTermsOfUse(
        LocalStorageManager.Instance.getObject<string>(
          AppConfig.Instance.LocalStorageKey["viewedTermsOfUse"],
        ),
      );
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === AppConfig.Instance.LocalStorageKey["viewedTermsOfUse"]) {
        setViewedTermsOfUse(
          LocalStorageManager.Instance.getObject<string>(
            AppConfig.Instance.LocalStorageKey["viewedTermsOfUse"],
          ),
        );
      }
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const disabledCheckbox = useMemo(
    () => !Boolean(viewedTermsOfUse),
    [viewedTermsOfUse],
  );

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      onSubmit={({ hasChecked }) => handleSubmit()}
    >
      <LinkContainer>
        <Link
          href={
            PageRoutes.LEGAL_FOR_USE_QUERY.replace(":query", "close") +
            "#term_of_service"
          }
          newTab
          text="利用規約"
          size="md"
          fontWeight={600}
          underline
        />

        <Checkbox
          text="利用規約を確認して、同意しました。"
          checkboxStyle="secondary"
          disabled={disabledCheckbox}
          onChange={setHasChecked}
        />
      </LinkContainer>

      <ButtonWrapper>
        <Button type="submit" text="アプリクーポンを使う" btnStyle="primary" />
      </ButtonWrapper>
    </FormTemplate>
  );
};

const LinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin: 20px 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
  margin-bottom: 12px;
  button {
    font-family: "Hiragino Sans";
    font-weight: 600;
    line-height: 1em;
    height: auto;
  }
`;
